import React from "react";
import { TWclassNames } from "./Div";

/**
 * Promang Loading & Motion System — inline loaders & skeletons.
 * See promang-docs/public/docs/core/PROMANG_LOADING_SYSTEM.md (§4.2, §4.7).
 *
 * Tokens (`--pls-*`) and keyframes (`pls-*`) live globally in src/App.css, so
 * everything here is theme-aware (light/dark) and respects reduced motion via
 * the `.pls-anim` / `.pls-skel` classes.
 *
 * Spinner variants (pick by context, don't invent new ones):
 *   - ring      → default, general actions / unknown waits
 *   - gradient  → premium / hero surfaces (conic masked ring)
 *   - dots      → tiny inline wait next to text
 *   - bars      → processing / media (equalizer)
 */

/** Single canonical spinner. `size` is the pixel diameter (default 36). */
export const Spinner = ({ variant = "ring", size = 36, className = "", style = {} }) => {
  if (variant === "dots") {
    const dot = (delay) => ({
      width: Math.max(6, size / 3.6),
      height: Math.max(6, size / 3.6),
      borderRadius: "50%",
      background: "var(--pls-accent, #1463C7)",
      animation: `pls-dots 1.2s ease-in-out ${delay}s infinite`,
    });
    return (
      <div
        className={TWclassNames("inline-flex items-center", className)}
        style={{ gap: Math.max(5, size / 5), ...style }}
        role="status"
        aria-label="Loading"
      >
        <span className="pls-anim" style={dot(0)} />
        <span className="pls-anim" style={dot(0.16)} />
        <span className="pls-anim" style={dot(0.32)} />
      </div>
    );
  }

  if (variant === "bars") {
    const bar = (delay) => ({
      width: Math.max(4, size / 7),
      height: size,
      borderRadius: 99,
      background:
        "linear-gradient(var(--pls-accent, #1463C7),var(--pls-glow, #35D6FF))",
      animation: `pls-bars 1s ease-in-out ${delay}s infinite`,
    });
    return (
      <div
        className={TWclassNames("inline-flex items-center", className)}
        style={{ gap: Math.max(4, size / 7), ...style }}
        role="status"
        aria-label="Loading"
      >
        <span className="pls-anim" style={bar(0)} />
        <span className="pls-anim" style={bar(0.15)} />
        <span className="pls-anim" style={bar(0.3)} />
        <span className="pls-anim" style={bar(0.45)} />
      </div>
    );
  }

  if (variant === "gradient") {
    return (
      <div
        className={TWclassNames("pls-anim", className)}
        role="status"
        aria-label="Loading"
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background:
            "conic-gradient(from 0deg,transparent,var(--pls-accent, #1463C7),var(--pls-glow, #35D6FF))",
          WebkitMask:
            "radial-gradient(farthest-side,transparent calc(100% - 5px),#000 0)",
          mask: "radial-gradient(farthest-side,transparent calc(100% - 5px),#000 0)",
          animation: "pls-spin .9s linear infinite",
          filter:
            "drop-shadow(0 0 6px color-mix(in srgb,var(--pls-accent, #1463C7) 55%,transparent))",
          ...style,
        }}
      />
    );
  }

  // ring (default)
  const border = Math.max(2.5, size / 10);
  return (
    <div
      className={TWclassNames("pls-anim", className)}
      role="status"
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `${border}px solid var(--pls-border, #E2E7F2)`,
        borderTopColor: "var(--pls-accent, #1463C7)",
        animation: "pls-spin .8s linear infinite",
        ...style,
      }}
    />
  );
};

export const ResourceLoading = ({
  size = "base",
  variant = "ring",
  loaderClass = "",
  baseClass = "top-0 left-0 right-0 bottom-0 w-full z-50 overflow-hidden flex flex-col items-center justify-center",
  position = "relative",
  isVisible = true,
  label,
}) => {
  if (isVisible === false) return null;

  if (size === "base") {
    return (
      <div
        className="mt-10 mb-10 w-full flex flex-col items-center justify-center gap-3"
        role="status"
        aria-live="polite"
      >
        <Spinner variant={variant} size={44} />
        <h2
          className="text-center text-sm font-semibold"
          style={{ color: "var(--pls-fg, #0A1432)" }}
        >
          {label ? label : "Loading..."}
        </h2>
      </div>
    );
  }

  const sizePx = size === "small" ? 24 : size === "large" ? 56 : 36;

  return (
    <div
      className={TWclassNames(
        baseClass,
        "flex items-center justify-center"
      )}
    >
      <Spinner
        variant={variant}
        size={sizePx}
        className={TWclassNames(loaderClass, position)}
      />
    </div>
  );
};

/** Indeterminate (unknown duration) progress bar. */
export const IndeterminateBar = ({ className = "", label }) => (
  <div className={TWclassNames("w-full", className)} role="status" aria-live="polite">
    <div
      style={{
        height: 8,
        borderRadius: 99,
        background: "var(--pls-surface2, #F4F6FC)",
        border: "1px solid var(--pls-border, #E2E7F2)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        className="pls-anim"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "38%",
          borderRadius: 99,
          background:
            "linear-gradient(90deg,transparent,var(--pls-accent, #1463C7),var(--pls-glow, #35D6FF),transparent)",
          animation: "pls-indet 1.4s ease-in-out infinite",
        }}
      />
    </div>
    {label && (
      <div
        style={{
          fontSize: 13,
          marginTop: 8,
          color: "var(--pls-muted, #6A7595)",
        }}
      >
        {label}
      </div>
    )}
  </div>
);

/** Determinate progress bar (known percentage). */
export const ProgressBar = ({ value = 0, className = "", showValue = true }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      className={TWclassNames("flex items-center", className)}
      style={{ gap: 14 }}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        style={{
          flex: 1,
          height: 8,
          borderRadius: 99,
          background: "var(--pls-surface2, #F4F6FC)",
          border: "1px solid var(--pls-border, #E2E7F2)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 99,
            background:
              "linear-gradient(90deg,var(--pls-accent, #1463C7),var(--pls-glow, #35D6FF))",
            width: `${pct}%`,
            transition: "width .2s linear",
          }}
        />
      </div>
      {showValue && (
        <span
          style={{
            fontWeight: 600,
            fontSize: 15,
            width: 42,
            textAlign: "right",
            color: "var(--pls-fg, #0A1432)",
          }}
        >
          {pct}%
        </span>
      )}
    </div>
  );
};

/** Single shimmering skeleton block. */
export const Skeleton = ({ width = "100%", height = 10, radius = 99, delay = 0, className = "", style = {} }) => (
  <div
    className={TWclassNames("pls-skel", className)}
    style={{
      width,
      height,
      borderRadius: radius,
      animationDelay: `${delay}s`,
      ...style,
    }}
  />
);

/**
 * Skeleton list for selector dialogs (avatar/icon + two text lines per row).
 * Prefer this over a centered spinner when the result list has a known shape.
 */
export const SelectorListSkeleton = ({ rows = 6 }) => (
  <div className="divide-y" style={{ borderColor: "var(--pls-border, #E2E7F2)" }} role="status" aria-live="polite" aria-label="Loading">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3">
        <Skeleton width={36} height={36} radius="50%" delay={i * 0.08} style={{ flex: "none" }} />
        <div className="flex-1 flex flex-col gap-2">
          <Skeleton width="55%" height={11} delay={i * 0.08 + 0.05} />
          <Skeleton width="32%" height={9} delay={i * 0.08 + 0.12} />
        </div>
      </div>
    ))}
  </div>
);
